<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import Draggable from 'vuedraggable';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import ConversationPipelineAPI from 'dashboard/api/conversationPipeline';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CrmConversationModal from '../components/CrmConversationModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const selectedProcessId = ref(null);
const kanban = ref(null);
const isLoading = ref(false);
const selectedConversation = ref(null);
const isConversationModalOpen = ref(false);

const processes = computed(() => getters['conversationPipeline/getProcesses'].value);
const selectedProcess = computed(() => {
  return (
    kanban.value?.process ||
    processes.value.find(process => process.id === selectedProcessId.value)
  );
});
const stages = computed(() => selectedProcess.value?.stages || []);

const conversationsByStage = ref({});

const groupConversationsByStage = conversations => {
  const groupedConversations = Object.fromEntries(
    stages.value.map(stage => [stage.id, []])
  );

  conversations.forEach(conversation => {
    stages.value.forEach(stage => {
      if (conversation.labels?.includes(stage.label.title)) {
        groupedConversations[stage.id].push(conversation);
      }
    });
  });

  return groupedConversations;
};

const loadKanban = async processId => {
  if (!processId) {
    kanban.value = null;
    return;
  }

  isLoading.value = true;
  try {
    const response = await ConversationPipelineAPI.getConversations(processId);
    kanban.value = response.data.payload;
    conversationsByStage.value = groupConversationsByStage(kanban.value.conversations);
  } catch (error) {
    kanban.value = null;
    conversationsByStage.value = {};
    useAlert(error?.response?.data?.message || t('CRM.KANBAN.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const moveConversation = async (event, stage) => {
  if (!event.added) return;

  isLoading.value = true;
  try {
    await ConversationPipelineAPI.moveConversation(
      selectedProcessId.value,
      event.added.element.id,
      stage.id
    );
  } catch (error) {
    useAlert(error?.response?.data?.message || t('CRM.KANBAN.MOVE_ERROR'));
  } finally {
    await loadKanban(selectedProcessId.value);
  }
};

const openConversation = conversation => {
  selectedConversation.value = conversation;
  isConversationModalOpen.value = true;
};

const closeConversation = () => {
  isConversationModalOpen.value = false;
  selectedConversation.value = null;
  store.dispatch('clearSelectedState');
};

watch(selectedProcessId, loadKanban);

onMounted(async () => {
  try {
    await store.dispatch('conversationPipeline/get');
  } catch (error) {
    useAlert(t('CRM.KANBAN.LOAD_ERROR'));
  }
});
</script>

<template>
  <main class="flex flex-col h-full gap-6 p-6 overflow-hidden">
    <header class="flex flex-col gap-1">
      <h1 class="text-heading-1 text-n-slate-12">{{ $t('CRM.KANBAN.TITLE') }}</h1>
      <p class="text-n-slate-11">{{ $t('CRM.KANBAN.DESCRIPTION') }}</p>
    </header>

    <label class="flex flex-col gap-1.5 max-w-md text-sm font-medium text-n-slate-12">
      {{ $t('CRM.KANBAN.SELECT_PROCESS') }}
      <select
        v-model.number="selectedProcessId"
        class="w-full px-3 py-2 rounded-md border bg-n-background border-n-weak text-n-slate-12"
      >
        <option :value="null">{{ $t('CRM.KANBAN.SELECT_PROCESS') }}</option>
        <option v-for="process in processes" :key="process.id" :value="process.id">
          {{ process.name }}
        </option>
      </select>
      <span v-if="selectedProcess?.team?.name" class="font-normal text-n-slate-11">
        {{ $t('CRM.KANBAN.TEAM', { team: selectedProcess.team.name }) }}
      </span>
    </label>

    <div v-if="!processes.length" class="p-8 text-center rounded-lg border border-dashed border-n-weak text-n-slate-11">
      {{ $t('CRM.KANBAN.NO_PROCESSES') }}
    </div>

    <div v-else-if="isLoading" class="flex items-center justify-center flex-1 gap-2 text-n-slate-11">
      <Spinner class="size-5" />
      {{ $t('CRM.KANBAN.LOADING') }}
    </div>

    <div v-else-if="!selectedProcessId" class="p-8 text-center rounded-lg border border-dashed border-n-weak text-n-slate-11">
      {{ $t('CRM.KANBAN.PROCESS_REQUIRED') }}
    </div>

    <div v-else-if="!stages.length" class="p-8 text-center rounded-lg border border-dashed border-n-weak text-n-slate-11">
      {{ $t('CRM.KANBAN.NO_STAGES') }}
    </div>

    <section v-else class="grid flex-1 grid-flow-col auto-cols-[minmax(18rem,1fr)] gap-4 overflow-x-auto pb-2">
      <article v-for="stage in stages" :key="stage.id" class="flex flex-col min-w-0 rounded-lg bg-n-slate-2">
        <header class="flex items-center justify-between gap-3 p-4 border-b border-n-weak">
          <div class="flex items-center min-w-0 gap-2">
            <span class="flex-none rounded-full bg-n-brand size-3" />
            <h2 class="font-medium truncate text-n-slate-12">{{ stage.name }}</h2>
          </div>
          <span class="flex-none text-sm text-n-slate-11">
            {{ $t('CRM.KANBAN.CONVERSATION_COUNT', { n: conversationsByStage[stage.id].length }) }}
          </span>
        </header>

        <Draggable
          v-model="conversationsByStage[stage.id]"
          item-key="id"
          group="crm-conversations"
          animation="200"
          class="flex flex-col flex-1 gap-3 p-3 overflow-y-auto"
          :disabled="isLoading"
          @change="moveConversation($event, stage)"
        >
          <template #item="{ element: conversation }">
            <button
              type="button"
              class="flex flex-col gap-2 p-4 text-left rounded-lg shadow-sm cursor-grab bg-n-background hover:bg-n-slate-1 active:cursor-grabbing"
              @click.stop="openConversation(conversation)"
            >
              <div class="flex items-start justify-between gap-3">
                <span class="font-medium truncate text-n-slate-12">{{ conversation.meta?.sender?.name }}</span>
                <span class="flex-none text-xs capitalize text-n-slate-11">{{ conversation.status }}</span>
              </div>
              <p class="text-sm line-clamp-2 text-n-slate-11">
                {{ conversation.messages?.[0]?.content || $t('CHAT_LIST.NO_MESSAGES') }}
              </p>
              <span v-if="conversation.meta?.assignee?.name" class="text-xs text-n-slate-11">
                {{ conversation.meta.assignee.name }}
              </span>
            </button>
          </template>
          <template #footer>
            <p v-if="!conversationsByStage[stage.id]?.length" class="p-4 text-sm text-center text-n-slate-11">
              {{ $t('CRM.KANBAN.NO_CONVERSATIONS') }}
            </p>
          </template>
        </Draggable>
      </article>
    </section>

    <CrmConversationModal
      :show="isConversationModalOpen"
      :conversation="selectedConversation"
      @close="closeConversation"
    />
  </main>
</template>
